import React from 'react';

import { Link } from 'react-router-dom';
import { truncate } from 'utils/formatting';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from 'components/search/Context';

import { EvseController } from 'types/evse-controller';

import { Table } from 'semantic';
import { formatLocationAddress } from '../../utils/formatting';
import { ConnectivityStatus } from '../../components/ConnectivityStatus';
import LocationStatusLabel from 'components/LocationStatusLabel';

const evseControllerStatusOrdered = [
  'UNKNOWN',
  'AVAILABLE',
  'PREPARING',
  'CHARGING',
  'SUSPENDEDEV',
  'SUSPENDEDEVSE',
  'FINISHING',
  'RESERVED',
  'UNAVAILABLE',
  'INOPERATIVE',
  'BLOCKED',
  'FAULTED',
];

const ocppToOcpiStatusMap = new Map([
  ['AVAILABLE', 'AVAILABLE'],
  ['PREPARING', 'CHARGING'],
  ['CHARGING', 'CHARGING'],
  ['SUSPENDEDEV', 'CHARGING'],
  ['SUSPENDEDEVSE', 'CHARGING'],
  ['FINISHING', 'CHARGING'],
  ['RESERVED', 'RESERVED'],
  ['FAULTED', 'OUTOFORDER'],
  ['BLOCKED', 'INOPERATIVE'],
  ['INOPERATIVE', 'INOPERATIVE'],
  ['UNAVAILABLE', 'UNKNOWN'],
]);

function ocppToOcpiStatus(ocppStatus: string) {
  return ocppToOcpiStatusMap.get(ocppStatus) ?? ocppStatus;
}

function determineEvseControllerStatus(item: EvseController) {
  let status = 'UNKNOWN';

  if (item.connectorStatus) {
    // we will pluck all connector statuses and select the worst one
    const allStatuses = Object.values(item.connectorStatus).map(
      (connectorStatus) => connectorStatus.status?.toUpperCase() || 'UNKNOWN'
    );

    const worstStatus = allStatuses.reduce((accumulator, current) => {
      return evseControllerStatusOrdered.indexOf(current) >
        evseControllerStatusOrdered.indexOf(accumulator)
        ? current
        : accumulator;
    }, 'UNKNOWN');

    status = ocppToOcpiStatus(worstStatus);
  }

  return <LocationStatusLabel status={status} />;
}

export default function EvseControllerMaintenaceTable() {
  const { items, loading } = useSearchContext<EvseController>();

  const { t } = useTranslation();

  if (!items.length || loading) return null;

  return (
    <Table celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell width={2}>
            {t('evseControllerMaintenance.ocppId', 'OCPP ID')}
          </Table.HeaderCell>
          <Table.HeaderCell width={3}>
            {t('evseControllerMaintenance.location', 'Location')}
          </Table.HeaderCell>
          <Table.HeaderCell width={2}>
            {t('evseControllerMaintenance.chargerStatus', 'Charger Status')}
          </Table.HeaderCell>
          <Table.HeaderCell width={2}>
            {t('evseControllerMaintenance.connectivity', 'Connectivity')}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {items.map((item) => {
          return (
            <Table.Row key={item.id}>
              <Table.Cell>
                <Link to={`/charging-stations/${item.id}`}>
                  {truncate(item.ocppIdentity, 20)}
                </Link>
                <p>
                  <small>
                    {t(
                      'evseControllerMaintenance.evseId',
                      'Evse ID: {{evseId}}',
                      {
                        evseId: item.evseId || '-',
                      }
                    )}
                  </small>

                  <br />
                  <small>
                    {t(
                      'evseControllerMaintenance.serialNumber',
                      'Serial number: {{serialNumber}}',
                      {
                        serialNumber: item.serialNumber || '-',
                      }
                    )}
                  </small>
                </p>
              </Table.Cell>
              <Table.Cell>
                {item.location ? (
                  <>
                    {item.location.name}, {formatLocationAddress(item.location)}
                    <br />
                  </>
                ) : (
                  '-'
                )}
              </Table.Cell>
              <Table.Cell textAlign="center">
                {determineEvseControllerStatus(item)}
              </Table.Cell>
              <Table.Cell textAlign="center">
                <ConnectivityStatus item={item} />
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}
